import React, { PureComponent } from 'react';
import * as d3 from "d3";
import { objIsEqual} from 'shared/helpers';

const padding = { t: 15, r: 10, b: 30, l: 40 };

class BarChart extends PureComponent {
    componentDidMount() {
        window.addEventListener('resize', this.resize)
        this.drawChart();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.resize)
    }

    componentDidUpdate(prevProps) {
        const { source,id} = this.props
        if (!objIsEqual(prevProps.source, source)) {
            // console.log(id)
            d3.select(this.refs[id]).select("svg").remove();
            // console.log("UPDATE")
            this.drawChart();
        }
        
    }

    drawChart = () => {
        const { source, height, id, color, numberOfTicks, paddingBetweenBars, tiltedLabels } = this.props;

        const svgWidth = parseInt(d3.select(this.refs[id]).style("width"))
        const svgHeight = height

        // Compute chart dimensions
        const chartWidth = svgWidth - padding.l - padding.r;
        const chartHeight = tiltedLabels ? svgHeight - padding.t - padding.b - 30 : svgHeight - padding.t - padding.b;

        this.svg = d3.select(this.refs[id])
            .append("svg")
            .attr("width", svgWidth)
            .attr("height", svgHeight);

        const chartG = this.svg.append('g')
            .attr('transform', 'translate(' + [padding.l, padding.t] + ')');

        const data = source;

        this.yScale = d3.scaleLinear()
            .domain([0, d3.max(data, d => d.count)])
            .range([chartHeight, 0])
            .nice();

        const yScale = this.yScale;

        this.xScale = d3.scaleBand()
            .domain(data.map(d => d.ordinals))
            .rangeRound([0, chartWidth])
            .padding(paddingBetweenBars);

        const xScale = this.xScale;

        this.yGrid = d3.axisLeft(yScale) // check this!
            .ticks(numberOfTicks)
            .tickSize(-chartWidth, 0, 0)
            .tickFormat('')

        chartG.append('g')
            .attr('class', 'y grid')
            .call(this.yGrid);

        this.xAxisG = chartG.append('g')
            .attr('class', 'x axis')
            .attr('transform', 'translate(' + [0, chartHeight] + ')');

        const yAxisG = chartG.append('g')
            .attr('class', 'y axis');

        const yAxis = d3.axisLeft(yScale)
            .ticks(numberOfTicks, 's')
            .tickSizeOuter(0);

        const xAxis = d3.axisBottom(xScale).tickSizeOuter(0)

        const bars = chartG.selectAll('.bar').data(data)

        this.barsEnter = bars.enter()
            .append('g')
            .attr('class', 'bar')
            .attr('transform', function (d, i) {
                var tx = xScale(d.ordinals);
                var ty = yScale(d.count);
                return 'translate(' + [tx, ty] + ')';
            });

        const barsEnter = this.barsEnter

        barsEnter.append('rect')
            .attr('height', d => chartHeight - yScale(d.count))
            .attr('width', xScale.bandwidth())
            .attr("fill", color)

        barsEnter.append('text')
            .attr('text-anchor', 'middle')
            .attr('x', xScale.bandwidth() / 2)
            .attr('dy', '-0.5em')
            .text(function (d) {
                return d.count.toLocaleString();
            });

        if (tiltedLabels) {
            this.xAxisG.call(xAxis)
                .selectAll(".tick text")
                .style("text-anchor", "start")
                .attr("dx", "0em")
                .attr("dy", ".22em")
                .attr("transform", "rotate(40)")
                .each(this.truncate);
        } else {
            this.xAxisG.call(xAxis)
                .selectAll(".tick text")
                .call(this.wrap, xScale.bandwidth() * 1);
        }

        yAxisG.call(yAxis)

    }

    resize = () => {
        const { id, tiltedLabels } = this.props;

        const svgWidth = parseInt(d3.select(this.refs[id]).style("width"));
        this.svg.attr("width", svgWidth)

        const chartWidth = svgWidth - padding.l - padding.r;

        this.xScale.rangeRound([0, chartWidth])

        const x = this.xScale;
        const y = this.yScale;

        this.barsEnter.attr('transform', function (d, i) {
            var tx = x(d.ordinals);
            var ty = y(d.count)
            return 'translate(' + [tx, ty] + ')';
        });

        this.barsEnter.selectAll('rect').attr('width', this.xScale.bandwidth())

        this.barsEnter.selectAll('text').attr('x', this.xScale.bandwidth() / 2)

        this.yGrid.tickSize(-chartWidth, 0, 0)

        this.svg.select('.y.grid').call(this.yGrid)

        const xAxis = d3.axisBottom(this.xScale).tickSizeOuter(0);

        if (tiltedLabels) {
            this.xAxisG.call(xAxis)
                .selectAll(".tick text")
                .style("text-anchor", "start")
                .attr("dx", "0em")
                .attr("dy", ".22em")
                .attr("transform", "rotate(40)")
                .each(this.truncate);
        } else {
            this.xAxisG.call(xAxis)
                .selectAll(".tick text")
                .call(this.wrap, this.xScale.bandwidth());
        }
    }

    truncate() {
        var self = d3.select(this),
            textLength = self.node().getComputedTextLength(),
            text = self.text();
        while (textLength > (80) && text.length > 0) {
            text = text.slice(0, -1);
            self.text(text + '...');
            textLength = self.node().getComputedTextLength();
        }
    };

    wrap(text, width) {
        text.each(function () {
            var text = d3.select(this),
                words = text.text().split(/\s+/).reverse(),
                word,
                line = [],
                lineNumber = 0,
                lineHeight = 1.1, // ems
                y = text.attr("y"),
                dy = parseFloat(text.attr("dy")),
                tspan = text.text(null).append("tspan").attr("x", 0).attr("y", y).attr("dy", dy + "em");
            // eslint-disable-next-line no-cond-assign
            while (word = words.pop()) {
                line.push(word);
                tspan.text(line.join(" "));
                if (tspan.node().getComputedTextLength() > width) {
                    line.pop();
                    tspan.text(line.join(" "));
                    line = [word];
                    tspan = text.append("tspan").attr("x", 0).attr("y", y).attr("dy", ++lineNumber * lineHeight + dy + "em").text(word);
                }
            }
        });
    }

    render() {
        const { id } = this.props;

        return <div className="chart" ref={id}/>
    } 
}

export default BarChart;